<template>
  <div id="order-create-page">
    <v-container>
      <div class="_fs-36">
        {{ title }}
      </div>

      <validation-observer ref="orderCreateForm">
        <template v-if="!isEdit">
          <order-package-product-list
            v-if="hasPackage"
            id="order-product-list"
            :products="filterProductsPackagePlan"
            :package-plan.sync="packagePlan"
            :package-month.sync="packageMonth"
            :package-started-at.sync="packageStartedAt"
            :min-package-started-at="packageStartedAt"
            :disabled="isEdit" />

          <div
            v-if="isOaChatPage"
            class="mb-8">
            <order-oa-chat-detail-table class="my-2">
            </order-oa-chat-detail-table>

            <order-oa-chat-product-list
              v-if="hasOaChat"
              id="order-oa-chat-list"
              :products="filterProductsOaChat"
              :oa-chat-month.sync="oaChatMonth"
              :oa-chat-started-at.sync="oaChatStartedAt"
              :min-oa-chat-started-at="oaChatStartedAt"
              :disabled="isEdit"
            />
          </div>

          <template v-if="hasPackage || isOaChatPage">
            <div class="mt-8 _fs-20">
              เลือกบริการเสริมที่คุณต้องการ
              <span class="grey--text">(เลือกได้มากกว่า 1 อย่าง)</span>
              <BaseRichTooltip
                max-width="500px"
                max-height="100vh"
                activator-icon="mdi-information-outline"
                activator-color="grey darken-1"
                icon-size="24"
                right
                align="start"
                :show-arrow="true"
                :arrow-size="8"
                arrow-color="white"
                arrow-border-color="#e0e0e0"
              >
                <template #activator="{ on, attrs }">
                  <v-icon
                    class="ml-1"
                    color="grey darken-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <ProductInformationContent
                  :show-oa-chat-package-info="!isOaChatPage"
                />
              </BaseRichTooltip>
            </div>
            <v-divider class="my-4" />
          </template>

          <BaseRichTooltip
            v-if="hasPackage || isOaChatPage"
            bottom
            align="start"
            max-width="500px"
            :show-arrow="true"
            :arrow-size="8"
            arrow-color="white"
            arrow-border-color="#e0e0e0"
            target=".v-input--selection-controls__input"
            target-position=".v-input--selection-controls__input"
            :disable-mobile="true"
          >
            <template #activator="{ on, attrs }">
              <div
                class="mx-2"
                v-bind="attrs"
                v-on="on"
              >
                <v-checkbox
                  v-model="hasMessage"
                  :disabled="isEdit"
                  :label="`ข้อความเพิ่มเติม (ข้อความละ ${messageUnitPrice} บาท)`"
                  :readonly="!hasPackage && isAccountFreePackagePlan"
                  @click="onClickMessageCheckbox()">
                </v-checkbox>
              </div>
            </template>
            <AdditionalMessageInfo />
          </BaseRichTooltip>
          <v-card
            v-if="hasMessage"
            class="mt-4 pa-10">
            <validation-provider
              v-slot="{ errors }"
              ref="messageQty"
              name="จำนวนข้อความเพิ่มเติม"
              mode="aggressive"
              :rules="{ required: true, additionalMessage: { messageProduct: findMessageProduct } }">
              <base-text-field
                v-model="messageQty"
                type="number"
                :disabled="isEdit"
                label="จำนวนข้อความเพิ่มเติม ที่ต้องการซื้อ"
                :error-messages="errors">
              </base-text-field>
            </validation-provider>
            <div class="font-weight-bold text-right primary--text _fs-20 text-right">
              {{ totalMessagePrice | price }} บาท
            </div>
            <div class="error--text text-right _fs-14">
              * ราคาดังกล่าวไม่รวมภาษีมูลค่าเพิ่ม 7%
            </div>
            <div class="error--text text-right _fs-14">
              ** ข้อความเพิ่มเติม หากใช้ไม่หมดจะถูกตัดตามระยะเวลาที่ซื้อแพ็กเกจหลักเข้ามา
            </div>
          </v-card>

          <div
            v-if="hasPackage || isOaChatPage"
          >
            <BaseRichTooltip
              bottom
              align="start"
              max-width="500px"
              :show-arrow="true"
              :arrow-size="8"
              arrow-color="white"
              arrow-border-color="#e0e0e0"
              target=".v-input--selection-controls__input"
              target-position=".v-input--selection-controls__input"
              :disable-mobile="true"
            >
              <template #activator="{ on, attrs }">
                <div
                  class="mx-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-checkbox
                    v-if="hasPackage || isOaChatPage"
                    v-model="hasPremiumId"
                    :disabled="isEdit"
                    label="Premium ID (444 บาท)">
                  </v-checkbox>
                </div>
              </template>
              <PremiumIdInfo />
            </BaseRichTooltip>
          </div>
          <v-card
            v-if="hasPremiumId"
            class="mt-4 pa-10">
            <v-row>
              <v-col cols="12">
                <v-radio-group
                  :value="isNewPremiumId"
                  @change="onUpdateNewPremiumId">
                  <v-radio
                    :value="false"
                    :disabled="!isRenewPremiumId || isBuyNewPremiumId"
                    label="ต่ออายุ" />
                  <div
                    v-if="!isRenewPremiumId"
                    class="error--text _fs-14">
                    * คุณยังไม่สามารถซื้อ Premium ID ล่วงหน้าได้ สามารถเริ่มซื้อได้ตั้งแต่วันที่
                    {{ minimumRenewPremiumIdDate }}
                  </div>
                  <v-radio
                    :value="true"
                    label="ซื้อใหม่/เปลี่ยนชื่อ" />
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <div class="">
                  <v-tooltip
                    v-model="showPremiumIdValidationTooltip"
                    top
                    color="#FFF"
                    content-class="tooltip-top"
                    attach
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                      >
                        <validation-provider
                          v-slot="{ errors }"
                          ref="premiumId"
                          name="Premium ID"
                          rules="required|premiumId">
                          <base-text-field
                            v-model="submitForm.premiumId"
                            :disabled="isEdit || !isNewPremiumId"
                            maxlength="19"
                            label="Premium ID"
                            :class="errors.length ? 'text-input-error': ''"
                            :error-messages="errors"
                            @focus="showPremiumIdValidationTooltip = true"
                            @blur="showPremiumIdValidationTooltip = false"
                            @input="checkPremiumIdValue"
                            @click="checkPremiumIdValue"
                          >
                          </base-text-field>
                        </validation-provider>
                      </div>
                    </template>
                    <div>
                      <div class="mt-1">
                        <div
                          v-if="premiumIdValidate.startWithAtSign"
                          class="primary--text _fs-16"
                        >
                          <v-icon
                            color="primary"
                          >
                            mdi-check-circle
                          </v-icon>
                          ขึ้นต้นด้วย @
                        </div>

                        <div
                          v-else
                          class="error--text _fs-16"
                        >
                          <v-icon
                            color="error"
                          >
                            mdi-close-circle
                          </v-icon>
                          ขึ้นต้นด้วย @
                        </div>
                      </div>

                      <div class="mt-2">
                        <div
                          v-if="premiumIdValidate.lowercase"
                          class="primary--text _fs-16"
                        >
                          <v-icon
                            color="primary"
                          >
                            mdi-check-circle
                          </v-icon>
                          ประกอบด้วย a-z (พิมพ์เล็กเท่านั้น)
                        </div>

                        <div
                          v-else
                          class="error--text _fs-16"
                        >
                          <v-icon
                            color="error"
                          >
                            mdi-close-circle
                          </v-icon>
                          ประกอบด้วย a-z (พิมพ์เล็กเท่านั้น)
                        </div>
                      </div>

                      <div class="mt-2">
                        <div
                          v-if="premiumIdValidate.numberAndSomeSpecialCharacter"
                          class="primary--text _fs-16"
                        >
                          <v-icon
                            color="primary"
                          >
                            mdi-check-circle
                          </v-icon>
                          ตัวเลข 0-9, . (Dot), - (Line), _ (Underscore) (ถ้ามี)
                        </div>

                        <div
                          v-else
                          class="error--text _fs-16"
                        >
                          <v-icon
                            color="error"
                          >
                            mdi-close-circle
                          </v-icon>
                          ตัวเลข 0-9, . (Dot), - (Line), _ (Underscore) (ถ้ามี)
                        </div>
                      </div>

                      <div class="mt-2">
                        <div
                          v-if="premiumIdValidate.validLength"
                          class="primary--text _fs-16"
                        >
                          <v-icon
                            color="primary"
                          >
                            mdi-check-circle
                          </v-icon>
                          ความยาว 4-18 อักษร
                        </div>

                        <div
                          v-else
                          class="error--text _fs-16"
                        >
                          <v-icon
                            color="error"
                          >
                            mdi-close-circle
                          </v-icon>
                          ความยาว 4-18 อักษร
                        </div>
                      </div>

                      <div class="mt-2">
                        <div
                          v-if="premiumIdValidate.noInvalidSpecialCharacter"
                          class="primary--text _fs-16"
                        >
                          <v-icon
                            color="primary"
                          >
                            mdi-check-circle
                          </v-icon>
                          ห้ามมีอักขระพิเศษ (!@#$%^&*+?&lt;>/:";',)
                        </div>

                        <div
                          v-else
                          class="error--text _fs-16"
                        >
                          <v-icon
                            color="error"
                          >
                            mdi-close-circle
                          </v-icon>
                          ห้ามมีอักขระพิเศษ (!@#$%^&*+?&lt;>/:";',)
                        </div>
                      </div>
                    </div>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col
                cols="12"
                sm="6">
                <base-date-picker
                  v-model="premiumIdStartedAt"
                  :disabled="isEdit"
                  label="วันที่ต้องการเปิดใช้งาน"
                  :min="minPremiumStart"
                  :max="maxPremiumStart"
                  hide-details>
                </base-date-picker>
              </v-col>
              <v-col
                cols="12"
                sm="6">
                <base-date-picker
                  :value="premiumIdExpired"
                  disabled
                  label="วันที่ Premium ID หมดอายุ"
                  hide-details
                  input-class="_green--text"
                >
                </base-date-picker>
              </v-col>
              <v-col cols="12">
                <div class="mt-4 error--text _fs-14">
                  <div>หมายเหตุ</div>
                  <div>* ราคาดังกล่าวไม่รวมภาษีมูลค่าเพิ่ม 7%</div>
                  <div>
                    * กรณีแจ้งวันเริ่มใช้งานเป็น วันหยุด หรือวันนักขัตฤกษ์
                    ระบบจะดำเนินการเปิดให้บริการในวันทำการถัดไป
                  </div>
                  <div>
                    * Premium ID สามารถเริ่มวันใดของเดือนก็ได้แต่แนะนำให้เริ่มในวันที่ 1 เพราะ LINE
                    Thailand จะมีการตัดวันใช้งานในวันสิ้นเดือนเสมอ (เช่นเริ่มในวันที่ 16 มีนาคม
                    อายุการใช้งานของเดือนแรกจะใช้ได้ตั้งแต่วันที่ 16-31 มีนาคม)
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>
          <div v-if="hasPackage">
            <BaseRichTooltip
              bottom
              align="start"
              max-width="500px"
              :show-arrow="true"
              :arrow-size="8"
              arrow-color="white"
              arrow-border-color="#e0e0e0"
              target=".v-input--selection-controls__input"
              target-position=".v-input--selection-controls__input"
              :disable-mobile="true"
            >
              <template #activator="{ on, attrs }">
                <div
                  class="mx-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-checkbox
                    v-if="hasPackage"
                    v-model="hasOaChat"
                    :disabled="isEdit"
                    label="OA Chat Package"
                    :readonly="hasPackage && activeLineOfficialAccount.oaChatExpiredAt != null"
                    @click="onClickOaChatCheckbox"
                  >
                  </v-checkbox>
                </div>
              </template>
              <OaChatPackageInfo />
            </BaseRichTooltip>
            <template
              v-if="hasOaChat"
              class="pa-10">
              <order-oa-chat-product-list
                v-if="hasOaChat"
                id="order-oa-chat-list"
                :products="filterProductsOaChat"
                :oa-chat-month.sync="oaChatMonth"
                :oa-chat-started-at.sync="oaChatStartedAt"
                :min-oa-chat-started-at="oaChatStartedAt"
                :disabled="isEdit" />
            </template>
          </div>
        </template>

        <template v-if="!isEdit">
          <template v-if="isNewLineOfficialAccountType">
            <div class="mt-8 _fs-20">
              คุณมีบัญชี LINE Official Account หรือไม่ ?
              <BaseRichTooltip
                max-width="500px"
                max-height="100vh"
                activator-icon="mdi-information-outline"
                activator-color="grey darken-1"
                icon-size="24"
                right
                align="start"
                :show-arrow="true"
                :arrow-size="8"
                arrow-color="white"
                arrow-border-color="#e0e0e0"
              >
                <template #activator="{ on, attrs }">
                  <v-icon
                    color="grey darken-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <TransferLineOfficialAccountInformationContent />
              </BaseRichTooltip>
            </div>
            <v-divider class="my-4"></v-divider>
            <validation-provider
              v-slot="{ errors }"
              ref="transferLineOfficialAccountType"
              rules="required"
              name="คุณมีบัญชี LINE Official Account หรือไม่ ?">
              <v-radio-group
                v-model="isTransferLineOfficialAccountType"
                row
                :error-messages="errors">
                <BaseRichTooltip
                  bottom
                  align="start"
                  max-width="500px"
                  :show-arrow="true"
                  :arrow-size="8"
                  arrow-color="white"
                  arrow-border-color="#e0e0e0"
                  target=".v-input--selection-controls__input"
                  target-position=".v-input--selection-controls__input"
                  :disable-mobile="true"
                >
                  <template #activator="{ on, attrs }">
                    <div
                      class="mx-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-radio
                        :value="true"
                        label="ย้ายบัญชีเข้า Sellsuki" />
                    </div>
                  </template>
                  <TransferLineOfficialAccountMoveInfo />
                </BaseRichTooltip>
                <BaseRichTooltip
                  bottom
                  align="start"
                  max-width="500px"
                  :show-arrow="true"
                  :arrow-size="8"
                  arrow-color="white"
                  arrow-border-color="#e0e0e0"
                  target=".v-input--selection-controls__input"
                  target-position=".v-input--selection-controls__input"
                  :disable-mobile="true"
                >
                  <template #activator="{ on, attrs }">
                    <div
                      class="mx-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-radio
                        :value="false"
                        label="เปิดบัญชีใหม่" />
                    </div>
                  </template>
                  <TransferLineOfficialAccountNewInfo />
                </BaseRichTooltip>
              </v-radio-group>
            </validation-provider>
          </template>

          <v-card
            v-if="isNewLineOfficialAccountType"
            class="mt-4 pa-10">
            <div class="_fs-20">
              Display Name
            </div>

            <v-row>
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  ref="displayName"
                  :rules="{
                    required: true
                  }"
                  name="Display Name">
                  <base-text-field
                    v-model="submitForm.displayName"
                    class="mt-4"
                    type="text"
                    maxlength="20"
                    label="Display Name"
                    :error-messages="errors"
                    @input="eventDisplayName">
                  </base-text-field>
                </validation-provider>
                <div class="error--text _fs-14">
                  <div>*** หมายเหตุ Display Name สามารถตั้งได้ไม่เกิน 20 ตัวอักษร</div>
                </div>
              </v-col>
            </v-row>

            <div v-if="isTransferLineOfficialAccountType && isNewLineOfficialAccountType">
              <div class="_fs-20">
                กรอก Basic ID เดิมของท่าน
              </div>

              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    ref="basicId"
                    :rules="{
                      required: true,
                      startWithAt: true,
                      lineBasicId: true
                    }"
                    name="Basic ID">
                    <base-text-field
                      id="basic-id"
                      v-model="submitForm.basicId"
                      class="mt-4"
                      label="Basic ID"
                      :error-messages="errors">
                    </base-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <div class="_fs-20">
                ช่องทางที่เคยชำระเงินมาก่อน
              </div>
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    ref="transferAgencyPaymentType"
                    rules="paymenttype"
                    name="ช่องทางที่เคยชำระเงิน">
                    <v-radio-group
                      v-model="submitForm.transferAgencyPaymentType"
                      :error-messages="errors"
                      @change="onUpdateTransferAgencyPaymentType">
                      <v-radio
                        value="lineThailand"
                        label="ชำระผ่าน LINE Thailand">
                      </v-radio>
                      <v-radio
                        value="other"
                        label="ชำระผ่าน Agency เจ้าอื่น">
                      </v-radio>
                    </v-radio-group>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row v-if="isTransferAgencyPayment">
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    ref="originalAgency"
                    :rules="{
                      required: true
                    }"
                    name="Agency เดิม">
                    <base-text-field
                      v-model="submitForm.originalAgency"
                      maxlength="20"
                      label="Agency เดิม"
                      :error-messages="errors">
                    </base-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </template>

        <div class="mt-4 _fs-20">
          กรอกข้อมูลของท่านให้ครบถ้วน
        </div>
        <v-divider class="my-4"></v-divider>
        <v-row v-if="!isEdit && !isNewLineOfficialAccountType">
          <v-col cols="12">
            <v-radio-group
              :value="isAutofillAddress"
              @change="onUpdateAutofillAddress">
              <v-radio :value="true">
                <template #label>
                  ใช้ข้อมูลจาก Basic ID
                  <span class="ml-1 font-weight-bold primary--text">
                    ({{ activeLineOfficialAccount.basicId }})
                  </span>
                </template>
              </v-radio>
              <v-radio
                :value="false"
                label="กรอกข้อมูลใหม่">
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <v-card class="pa-10">
          <template v-if="!isEdit && isAutofillAddress && !isEditAutofillAddress">
            <v-btn
              class="d-sm-none"
              color="primary"
              absolute
              right
              icon
              @click="setIsEditAutofillAddress(true)">
              <v-icon>
                mdi-pencil
              </v-icon>
            </v-btn>
            <v-btn
              class="d-none d-sm-block"
              color="primary"
              absolute
              right
              text
              rounded
              min-width="150"
              @click="setIsEditAutofillAddress(true)">
              <v-icon left>
                mdi-pencil
              </v-icon>
              แก้ไขข้อมูลการสั่งซื้อ
            </v-btn>

            <base-user-profile-template
              v-bind="submitForm"
              :is-juristic-customer="isJuristicCustomer"
              :is-headquarter="isHeadquarter"
              :citizen-id-text="$utils.citizenIdText(isJuristicCustomer)"
              :customer-type-text="
                $utils.customerTypeText(customerTypes, submitForm.customerTypeId)
              ">
            </base-user-profile-template>
          </template>

          <template v-else-if="!isAutofillAddress || (isAutofillAddress && isEditAutofillAddress)">
            <div class="mb-4 _fs-20">
              ข้อมูลร้านค้า หรือบริษัท
            </div>
            <v-row>
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  ref="juristicCustomer"
                  rules="required"
                  name="บุคคลธรรมดาหรือนิติบุคคล">
                  <v-radio-group
                    v-model="isJuristicCustomer"
                    :error-messages="errors"
                    @change="onUpdateCustomerType">
                    <v-radio
                      :value="false"
                      label="บุคคลธรรมดา">
                    </v-radio>
                    <v-radio
                      :value="true"
                      label="นิติบุคคล">
                    </v-radio>
                  </v-radio-group>
                </validation-provider>
              </v-col>

              <template>
                <v-col
                  v-if="isJuristicCustomer"
                  cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    ref="customerTypeId"
                    rules="required"
                    name="ประเภทของนิติบุคคล">
                    <v-select
                      v-model="submitForm.customerTypeId"
                      label="ประเภทของนิติบุคคล"
                      filled
                      rounded
                      :error-messages="errors"
                      :items="filterAndSortJuristicCustomerTypes">
                    </v-select>
                  </validation-provider>
                </v-col>

                <v-col
                  v-if="isJuristicCustomer !== undefined"
                  cols="12"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    ref="citizenId"
                    :rules="{ required: true, citizenId: { isJuristicCustomer } }"
                    :name="$utils.citizenIdText(isJuristicCustomer)">
                    <base-text-field
                      v-model="submitForm.citizenId"
                      :label="cizenIdText"
                      type="tel"
                      maxlength="13"
                      :error-messages="errors"
                      @change="onUpdateCitizenId">
                    </base-text-field>
                  </validation-provider>
                  <v-btn
                    v-if="isJuristicCustomer"
                    color="primary"
                    width="200"
                    rounded
                    @click="findRevenue">
                    ตรวจสอบเลขประจำตัว
                  </v-btn>
                </v-col>

                <v-col
                  v-if="isJuristicCustomer"
                  cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    ref="companyName"
                    rules="required"
                    name="ชื่อร้านค้า หรือชื่อบริษัท">
                    <base-text-field
                      v-model="submitForm.companyName"
                      type="text"
                      label="ชื่อร้านค้า หรือชื่อบริษัท"
                      :error-messages="errors">
                    </base-text-field>
                  </validation-provider>
                  <v-radio-group
                    :value="isHeadquarter"
                    @change="onUpdateIsHeadquarter">
                    <v-radio
                      :value="true"
                      label="สำนักงานใหญ่">
                    </v-radio>
                    <v-radio
                      :value="false"
                      label="สาขาย่อย">
                    </v-radio>
                  </v-radio-group>
                  <validation-provider
                    v-slot="{ errors }"
                    ref="branchNumber"
                    rules="required|length:5|integer"
                    name="เลขที่สาขา">
                    <base-text-field
                      v-model="submitForm.branchNumber"
                      :disabled="isHeadquarter || isHeadquarter === undefined"
                      label="เลขที่สาขา"
                      type="tel"
                      maxlength="5"
                      :error-messages="errors">
                    </base-text-field>
                  </validation-provider>
                </v-col>
              </template>
            </v-row>

            <div class="mb-4 _fs-20">
              ข้อมูลผู้ติดต่อ
            </div>

            <v-row>
              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="firstName"
                  rules="required"
                  name="ชื่อ">
                  <base-text-field
                    v-model="submitForm.firstName"
                    label="ชื่อ"
                    :error-messages="errors">
                  </base-text-field>
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="lastName"
                  rules="required"
                  name="นามสกุล">
                  <base-text-field
                    v-model="submitForm.lastName"
                    label="นามสกุล"
                    :error-messages="errors">
                  </base-text-field>
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6"
                class="pt-md-11">
                <validation-provider
                  v-slot="{ errors }"
                  ref="email"
                  rules="required|email"
                  name="Email สำหรับส่งใบกำกับภาษี (E-Tax Online)">
                  <base-text-field
                    v-model="submitForm.email"
                    label="Email สำหรับส่งใบกำกับภาษี (E-Tax Online)"
                    type="email"
                    :error-messages="errors">
                  </base-text-field>
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <v-checkbox
                  v-model="useSameEmail"
                  label="Email เดียวกับที่ส่งใบกำกับภาษี"
                  hide-details
                  style="margin: 0px; padding: 4px 20px;"
                  @change="onUseSameEmail"
                />
                <validation-provider
                  v-slot="{ errors }"
                  ref="emailQuotation"
                  rules="required|email"
                  name="Email สำหรับส่งใบเสนอราคา">
                  <base-text-field
                    v-model="submitForm.emailQuotation"
                    label="Email สำหรับส่งใบเสนอราคา"
                    type="email"
                    :error-messages="errors">
                  </base-text-field>
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="tel"
                  rules="required|integer"
                  name="เบอร์โทรศัพท์">
                  <base-text-field
                    v-model="submitForm.tel"
                    label="เบอร์โทรศัพท์"
                    type="tel"
                    maxlength="10"
                    :error-messages="errors">
                  </base-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <div class="mb-4 _fs-20">
              ที่อยู่ของร้านค้า หรือบริษัท
            </div>
            <v-row>
              <v-col cols="12">
                <validation-provider
                  v-slot="{ errors }"
                  ref="address"
                  rules="required"
                  name="ที่อยู่">
                  <base-text-field
                    v-model="submitForm.address"
                    type="text"
                    label="ที่อยู่ (ระบุเลขที่ อาคาร ชั้นที่ ห้องที่ หมู่ หรือซอย)"
                    :error-messages="errors">
                  </base-text-field>
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <base-text-field
                  v-model="submitForm.street"
                  type="text"
                  label="ถนน"
                  is-optional>
                </base-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="subdistrict"
                  rules="required"
                  name="แขวง/ตำบล">
                  <base-thai-address-autocomplete
                    v-model="submitForm.subdistrict"
                    label="แขวง/ตำบล"
                    address-type="subdistrict"
                    :error-messages="errors"
                    type="text"
                    @select="onSelectAddress" />
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="district"
                  rules="required"
                  name="เขต/อำเภอ">
                  <base-thai-address-autocomplete
                    v-model="submitForm.district"
                    label="เขต/อำเภอ"
                    address-type="district"
                    :error-messages="errors"
                    type="text"
                    @select="onSelectAddress" />
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="stateProvince"
                  rules="required"
                  name="จังหวัด">
                  <base-thai-address-autocomplete
                    v-model="submitForm.stateProvince"
                    label="จังหวัด"
                    address-type="stateProvince"
                    :error-messages="errors"
                    type="text"
                    @select="onSelectAddress" />
                </validation-provider>
              </v-col>

              <v-col
                cols="12"
                md="6">
                <validation-provider
                  v-slot="{ errors }"
                  ref="zipCode"
                  rules="required|length:5|integer"
                  name="รหัสไปรษณีย์">
                  <base-thai-address-autocomplete
                    v-model="submitForm.zipCode"
                    label="รหัสไปรษณีย์"
                    address-type="zipCode"
                    :error-messages="errors"
                    type="tel"
                    maxlength="5"
                    @select="onSelectAddress" />
                </validation-provider>
              </v-col>
            </v-row>
          </template>
        </v-card>
      </validation-observer>
      <div class="my-8 text-center">
        <v-btn
          color="primary"
          width="150"
          rounded
          @click="routeToOrderSummary">
          {{ submitButtonText }}
        </v-btn>
      </div>

      <order-line-thailand
        :active.sync="lineThailandActive"
        @accept="onAcceptLineThailand"
        @cancel="onCancelLineThailand">
      </order-line-thailand>
      <order-other-agency
        :active.sync="otherAgencyActive"
        @accept="onAcceptOtherAgency"
        @cancel="onCancelOtherAgency">
      </order-other-agency>
    </v-container>
  </div>
</template>

<script>
import { mapWaitingActions } from 'vue-wait'
import { mapActions, mapState, mapGetters } from 'vuex'
import store from '@/store'
import { HEADQUARTER_BRANCH_NUMBER, NORMAL_CUSTOMER_TYPE_VALUE } from '@/utils/defaultValue'
import { isInRenewPremiumIdDate, isBuyNewPremiumIdByNewAccount, isInRenewOaChatDate, isBuyNewOaChatByNewAccount } from '@/utils/order.util'

const defaultSubmitForm = {
  displayName: undefined,
  basicId: undefined,
  originalAgency: undefined,
  firstName: undefined,
  lastName: undefined,
  branchNumber: HEADQUARTER_BRANCH_NUMBER,
  companyName: undefined,
  customerTypeId: NORMAL_CUSTOMER_TYPE_VALUE,
  citizenId: undefined,
  address: undefined,
  street: undefined,
  subdistrict: undefined,
  district: undefined,
  stateProvince: undefined,
  zipCode: undefined,
  email: undefined,
  emailQuotation: undefined,
  tel: undefined,
  premiumId: undefined,
  transferAgencyPaymentType: undefined
}

export default {
  name: 'OrderCreateEditPage',
  components: {
    BaseDatePicker: () => import('@/components/BaseDatePicker.vue'),
    BaseTextField: () => import('@/components/BaseTextField.vue'),
    BaseThaiAddressAutocomplete: () => import('@/components/BaseThaiAddressAutocomplete.vue'),
    BaseUserProfileTemplate: () => import('@/components/BaseUserProfileTemplate.vue'),
    OrderPackageProductList: () => import('@/components/OrderPackageProductList.vue'),
    OrderOaChatProductList: () => import('@/components/OrderOaChatProductList.vue'),
    OrderLineThailand: () => import('@/components/OrderLineThailand.vue'),
    OrderOtherAgency: () => import('@/components/OrderOtherAgency.vue'),
    OrderOaChatDetailTable: () => import('@/components/OrderOaChatDetailTable.vue'),
    BaseRichTooltip: () => import('@/components/Base/BaseRichTooltip.vue'),
    ProductInformationContent: () => import('@/components/Tooltip/ProductInformationContent.vue'),
    AdditionalMessageInfo: () => import('@/components/ProductInfo/AdditionalMessageInfo.vue'),
    PremiumIdInfo: () => import('@/components/ProductInfo/PremiumIdInfo.vue'),
    OaChatPackageInfo: () => import('@/components/ProductInfo/OaChatPackageInfo.vue'),
    TransferLineOfficialAccountInformationContent: () => import('@/components/Tooltip/TransferLineOfficialAccountInformationContent.vue'),
    TransferLineOfficialAccountMoveInfo: () => import('@/components/LineOfficialAccount/TransferLineOfficialAccountMoveInfo.vue'),
    TransferLineOfficialAccountNewInfo: () => import('@/components/LineOfficialAccount/TransferLineOfficialAccountNewInfo.vue')
  },
  props: {
    id: {
      type: String,
      default: ''
    }
  },
  data () {
    const tomorrowDate = this.$utils.createDayjsDate()
      .add(1, 'day')
      .startOf('day')
      .toDate()
    const threeMonth = this.$utils.createDayjsDate()
      .add(3, 'month')
      .endOf('month')
      .toDate()

    return {
      useSameEmail: false,
      packagePlan: undefined,
      packageMonth: undefined,
      oaChatMonth: undefined,
      messageQty: undefined,
      submitForm: JSON.parse(JSON.stringify(defaultSubmitForm)),
      isAutofillAddress: false,
      isEditAutofillAddress: false,
      isNewPremiumId: false,
      isNewOaChat: false,
      packageStartedAt: tomorrowDate,
      premiumIdStartedAt: tomorrowDate,
      oaChatStartedAt: tomorrowDate,
      minPremiumStart: tomorrowDate,
      maxPremiumStart: threeMonth,
      minOaChatStart: tomorrowDate,
      maxOaChatStart: threeMonth,
      hasMessageValue: undefined,
      hasPremiumIdValue: undefined,
      hasOaChatValue: undefined,
      isTransferLineOfficialAccountType: undefined,
      isJuristicCustomer: undefined,
      isHeadquarter: undefined,
      isTransferAgencyPayment: false,
      lineThailandActive: false,
      isLineThailandAccepted: false,
      otherAgencyActive: false,
      isOtherAgencyAccepted: false,
      displayName: undefined,
      minimumRenewPremiumIdDate: undefined,
      minimumRenewOaChatDate: undefined,
      showPremiumIdValidationTooltip: false,
      premiumIdValidate: {
        startWithAtSign: false,
        lowercase: false,
        numberAndSomeSpecialCharacter: false,
        validLength: false,
        noInvalidSpecialCharacter: true
      }
    }
  },
  computed: {
    ...mapState('CustomerType', ['customerTypes']),
    ...mapState('Order', ['createOrderForm', 'order']),
    ...mapState('Product', ['products']),
    ...mapGetters({
      hasLineOfficialAccount: 'User/hasLineOfficialAccount',
      ownLineOfficialAccounts: 'User/ownLineOfficialAccounts',
      activeLineOfficialAccount: 'User/activeLineOfficialAccount',
      MAXIMUM_PERIOD_PRE_ORDER: 'SystemConfig/GET_MAXIMUM_PERIOD_PRE_ORDER'
    }),
    isRenew () {
      return !!this.$route.query.isRenew
    },
    isEdit () {
      return this.$route.meta.isEdit
    },
    title () {
      if (this.isEdit) {
        return 'แก้ไขข้อมูลผู้ซื้อ'
      }
      const titleData = {
        package: this.isRenew ? 'ต่ออายุแพ็กเกจ' : 'ซื้อแพ็กเกจ',
        message: 'ซื้อ Message',
        premiumId: 'ซื้อ Premium ID',
        newLineOfficialAccount: 'ลูกค้าใหม่',
        oaChat: 'ซื้อ OA Chat'
      }

      return titleData[this.productType] || ''
    },
    submitButtonText () {
      if (this.isEdit) {
        return 'ถัดไป'
      }

      return 'ยืนยัน'
    },
    mapProducts () {
      return this.products.map(({ _id, ...product }) => ({ product: _id, ...product }))
    },
    productType () {
      return this.$route.params.productType
    },
    isNewLineOfficialAccountType () {
      return this.productType === 'newLineOfficialAccount'
    },
    isOaChatPage () {
      return this.productType === 'oaChat'
    },
    isMessagePage () {
      return this.productType === 'message'
    },
    isRenewPremiumId () {
      return isInRenewPremiumIdDate(this.activeLineOfficialAccount, this.MAXIMUM_PERIOD_PRE_ORDER)
    },
    isBuyNewPremiumId() {
      return isBuyNewPremiumIdByNewAccount(this.activeLineOfficialAccount)
    },
    isRenewOaChat () {
      return isInRenewOaChatDate(this.activeLineOfficialAccount, this.MAXIMUM_PERIOD_PRE_ORDER)
    },
    isBuyNewOaChat() {
      return isBuyNewOaChatByNewAccount(this.activeLineOfficialAccount)
    },
    hasPackage () {
      return this.productType === 'package' || this.isNewLineOfficialAccountType || this.productType === 'transferLineOfficialAccount'
    },
    isAccountFreePackagePlan () {
      return this.activeLineOfficialAccount.packagePlan === 'free'
    },
    hasMessage: {
      get () {
        return typeof this.hasMessageValue === 'boolean'
          ? this.hasMessageValue
          : this.productType === 'message'
      },
      set (value) {
        this.hasMessageValue = value
      }
    },
    hasPremiumId: {
      get () {
        return typeof this.hasPremiumIdValue === 'boolean'
          ? this.hasPremiumIdValue
          : this.productType === 'premiumId'
      },
      set (value) {
        this.hasPremiumIdValue = value
      }
    },
    hasOaChat: {
      get () {
        return typeof this.hasOaChatValue === 'boolean'
          ? this.hasOaChatValue
          : this.productType === 'oaChat'
      },
      set (value) {
        this.hasOaChatValue = value
      }
    },
    premiumIdExpired() {
      return this.$utils.createDayjsDate(this.premiumIdStartedAt).add((11), 'month').endOf('month').toDate()
    },
    checkJuristicCustomer () {
      return this.submitForm.customerTypeId !== this.$default.NORMAL_CUSTOMER_TYPE_VALUE
    },
    cizenIdText () {
      return this.isJuristicCustomer
        ? 'เลขประจำตัวผู้เสียภาษี'
        : 'เลขประจำตัวประชาชน'
    },
    filterAndSortJuristicCustomerTypes () {
      const filteredCustomerTypes = this.customerTypes.filter(
        ({ value }) => value !== this.$default.NORMAL_CUSTOMER_TYPE_VALUE
      )

      return filteredCustomerTypes.sort((a, b) => {
        if (a.index < b.index) return -1
        if (a.index > b.index) return 1
        return 0
      })
    },
    filterProductsPackagePlan () {
      return this.mapProducts
        .filter((product) => product.productType === 'package' && product.packagePlan === this.packagePlan)
        .sort((a, b) => a.packageMonth - b.packageMonth)
    },
    findPackageProduct () {
      return this.filterProductsPackagePlan.find(
        (item) => item.packagePlan === this.packagePlan && item.packageMonth === this.packageMonth
      )
    },
    filterProductsOaChat () {
      return this.mapProducts
        .filter((product) => product.productType === 'oaChat')
        .sort((a, b) => a.oaChatMonth - b.oaChatMonth)
    },
    findMessageProduct () {
      if (this.isOaChatPage || this.isMessagePage) {
        return this.mapProducts.find((product) => product.productType === 'message' && product.packagePlan === this.activeLineOfficialAccount.packagePlan)
      }
      return this.mapProducts.find((product) => product.productType === 'message' && product.packagePlan === this.packagePlan)
    },
    findPremiumIdProduct () {
      return this.mapProducts.find((product) => product.productType === 'premiumId')
    },
    findOaChatProduct () {
      return this.mapProducts.find((product) => product.productType === 'oaChat'
        && product.oaChatMonth === this.oaChatMonth)
    },
    messageUnitPrice () {
      return this.findMessageProduct ? this.findMessageProduct.unitPrice : 0
    },
    totalMessagePrice () {
      if (!this.messageQty) {
        return 0
      }

      return parseInt(this.messageQty, 10) * this.messageUnitPrice
    }
  },
  async beforeRouteEnter (to, from, next) {
    store.dispatch('wait/start', 'app loading', { root: true })

    const fetchPromises = [
      store.dispatch('Product/fetchProducts'),
      store.dispatch('SystemConfig/fetchSystemConfigs', 'MAXIMUM_PERIOD_PRE_ORDER')
    ]

    if (store.getters['CustomerType/isCustomerTypesEmpty']) {
      fetchPromises.push(store.dispatch('CustomerType/fetchCustomerTypes'))
    }

    if (to.meta.isEdit) {
      fetchPromises.push(store.dispatch('Order/fetchOrderById', to.params.id))
    }

    await Promise.all(fetchPromises)

    store.dispatch('wait/end', 'app loading', { root: true })
    next()
  },
  beforeRouteUpdate (to, from, next) {
    this.hasMessageValue = undefined
    this.hasPremiumIdValue = undefined
    this.hasOaChatValue = undefined
    next()
  },
  watch: {
    // eslint-disable-next-line func-names
    'submitForm.email': function(value) {
      if (this.useSameEmail) {
        this.$set(this.submitForm, 'emailQuotation', value)
      }
    }
  },
  created () {
    this.initiate()
  },
  methods: {
    ...mapActions({
      fetchLineOfficialAccountByBasicId: 'LineOfficialAccount/fetchLineOfficialAccountByBasicId',
      fetchRevenue: 'Order/fetchRevenue',
      setCreateOrderForm: 'Order/setCreateOrderForm'
    }),
    ...mapWaitingActions('LineOfficialAccount', {
      fetchLineOfficialAccountByBasicId: 'app loading'
    }),
    async initiate () {
      this.maxPremiumStart = this.$utils.createDayjsDate().add(
        this.MAXIMUM_PERIOD_PRE_ORDER.value,
        this.MAXIMUM_PERIOD_PRE_ORDER.unit
      ).toDate()

      if (this.isRenew && this.hasPackage) {
        this.packageStartedAt = this.$utils.createDayjsDate(this.activeLineOfficialAccount.packageExpiredAt).add(1, 'day').toDate()
      } else if (this.hasMessage) {
        this.packagePlan = this.activeLineOfficialAccount.packagePlan
      }

      this.isNewPremiumId = this.activeLineOfficialAccount.premiumId === undefined
        || this.$utils.createDayjsDate(this.activeLineOfficialAccount.premiumIdExpiredAt).isBefore(this.$utils.createDayjsDate())
        || !isInRenewPremiumIdDate(this.activeLineOfficialAccount, this.MAXIMUM_PERIOD_PRE_ORDER)

      if (!this.isNewPremiumId) {
        this.submitForm.premiumId = this.activeLineOfficialAccount.premiumId
        this.premiumIdStartedAt = this.$utils.createDayjsDate(this.activeLineOfficialAccount.premiumIdExpiredAt).add(1, 'day').toDate()
        this.minPremiumStart = this.$utils.createDayjsDate(this.activeLineOfficialAccount.premiumIdExpiredAt).add(1, 'day').toDate()
        this.maxPremiumStart = this.$utils.createDayjsDate(this.activeLineOfficialAccount.premiumIdExpiredAt).add(
          this.MAXIMUM_PERIOD_PRE_ORDER.value, this.MAXIMUM_PERIOD_PRE_ORDER.unit
        ).toDate()
      }

      if (!this.isRenewPremiumId) {
        this.minimumRenewPremiumIdDate = this.$options.filters.dateTimeFormat(
          this.$utils.createDayjsDate(this.activeLineOfficialAccount.premiumIdExpiredAt)
            .subtract(this.MAXIMUM_PERIOD_PRE_ORDER.value, this.MAXIMUM_PERIOD_PRE_ORDER.unit).set('date', 1)
        )
      }

      this.isNewOaChat = this.activeLineOfficialAccount.oaChatStartedAt === undefined
        || this.$utils.createDayjsDate(this.activeLineOfficialAccount.oaChatExpiredAt).isBefore(this.$utils.createDayjsDate())
        || !isInRenewOaChatDate(this.activeLineOfficialAccount, this.MAXIMUM_PERIOD_PRE_ORDER)

      if (!this.isNewOaChat) {
        this.oaChatMonth = this.activeLineOfficialAccount.oaChatMonth
        this.oaChatStartedAt = this.$utils.createDayjsDate(this.activeLineOfficialAccount.oaChatExpiredAt).add(1, 'day').toDate()
        this.minOaChatStart = this.$utils.createDayjsDate(this.activeLineOfficialAccount.oaChatExpiredAt).add(1, 'day').toDate()
        this.maxOaChatStart = this.$utils.createDayjsDate(this.activeLineOfficialAccount.oaChatExpiredAt).add(
          this.MAXIMUM_PERIOD_PRE_ORDER.value, this.MAXIMUM_PERIOD_PRE_ORDER.unit
        ).toDate()
      }

      if (!this.isRenewOaChat) {
        this.minimumRenewOaChatDate = this.$options.filters.dateTimeFormat(
          this.$utils.createDayjsDate(this.activeLineOfficialAccount.oaChatExpiredAt)
            .subtract(this.MAXIMUM_PERIOD_PRE_ORDER.value, this.MAXIMUM_PERIOD_PRE_ORDER.unit).set('date', 1)
        )
      }

      if (Object.keys(this.createOrderForm).length > 0) {
        this.prepareSubmitForm(this.createOrderForm)
      } else if (this.isEdit && this.hasMessage) {
        const { orderItems, ...filterOrderData } = this.order
        const { packagePlan } = this.findProductByType(orderItems, 'message')

        this.prepareSubmitForm({
          orderItems, packagePlan, ...filterOrderData
        })
      } else if (this.isEdit && this.hasPremiumId) {
        const { orderItems, ...filterOrderData } = this.order
        const { premiumId } = this.findProductByType(orderItems, 'premiumId')

        this.prepareSubmitForm({
          orderItems, premiumId, ...filterOrderData
        })
      } else if (this.isEdit && this.hasOaChat) {
        const { orderItems, ...filterOrderData } = this.order
        // TODO:: finish this
        const { oaChat } = this.findProductByType(orderItems, 'oaChat')

        this.prepareSubmitForm({
          orderItems, oaChat, ...filterOrderData
        })
      } else if (this.isEdit) {
        const { orderItems, type, ...filterOrderData } = this.order
        const { packagePlan, packageMonth, startedAt } = this.findProductByType(orderItems, 'package')

        const isTransferLineOfficialAccountType = type === 'transferLineOfficialAccount'

        this.prepareSubmitForm({
          orderItems,
          packagePlan,
          packageMonth,
          packageStartedAt: startedAt,
          type,
          isTransferLineOfficialAccountType,
          ...filterOrderData
        })
      } else if (!this.isNewLineOfficialAccountType) {
        this.isAutofillAddress = true
        this.autofillAddress()
      }
    },
    onUseSameEmail() {
      if (this.useSameEmail) {
        this.$set(this.submitForm, 'emailQuotation', this.submitForm.email)
      } else {
        this.$set(this.submitForm, 'emailQuotation', undefined)
      }
    },
    prepareSubmitForm ({
      orderItems, packagePlan, packageMonth, packageStartedAt,
      isAutofillAddress, isEditAutofillAddress, isTransferLineOfficialAccountType,
      oaChat, ...orderData
    }) {
      this.isAutofillAddress = isAutofillAddress
      this.isEditAutofillAddress = isEditAutofillAddress
      this.isTransferLineOfficialAccountType = isTransferLineOfficialAccountType
      this.submitForm = JSON.parse(JSON.stringify(orderData))
      this.packagePlan = packagePlan
      this.packageMonth = packageMonth
      this.packageStartedAt = packageStartedAt
      this.isJuristicCustomer = this.checkJuristicCustomer
      this.isHeadquarter = this.submitForm.branchNumber === this.$default.HEADQUARTER_BRANCH_NUMBER

      const messageData = this.findProductByType(orderItems, 'message')
      if (messageData) {
        this.messageQty = messageData.itemQty
        this.hasMessage = true
      }

      const premiumIdData = this.findProductByType(orderItems, 'premiumId')
      if (premiumIdData) {
        this.premiumIdStartedAt = premiumIdData.startedAt
        this.hasPremiumId = true
      }

      const oaChatData = this.findProductByType(orderItems, 'oaChat')
      if (oaChatData) {
        this.oaChatMonth = oaChatData.oaChatMonth
        this.oaChatStartedAt = oaChatData.startedAt
        this.hasOaChat = true
      }
    },
    setIsEditAutofillAddress (value) {
      this.isEditAutofillAddress = value
    },
    autofillAddress () {
      const {
        _id,
        status,
        premiumId,
        ...filterActiveLineOfficialAccount
      } = this.activeLineOfficialAccount

      this.submitForm = {
        premiumId: this.submitForm.premiumId,
        ...filterActiveLineOfficialAccount
      }

      if (this.isAutofillAddress) {
        const isJuristicCustomer = this.checkJuristicCustomer
        this.$set(this, 'isJuristicCustomer', isJuristicCustomer)
        if (isJuristicCustomer) {
          const isHeadquarter = this.submitForm.branchNumber
            === this.$default.HEADQUARTER_BRANCH_NUMBER
          this.$set(this, 'customerTypeId', this.submitForm.customerTypeId)
          this.$set(this, 'isHeadquarter', isHeadquarter)
        }
      }
    },
    eventDisplayName (event) {
      this.$nextTick(() => {
        if (event[event.length - 1] === ' ' && event[event.length - 2] === ' ') {
          this.submitForm.displayName = event.substring(0, event.length - 1)
        } else {
          this.submitForm.displayName = event
        }
      })
    },
    onUpdateTransferAgencyPaymentType () {
      this.isTransferAgencyPayment = this.submitForm.transferAgencyPaymentType === 'other'
      this.otherAgencyActive = this.submitForm.transferAgencyPaymentType === 'other'
      this.lineThailandActive = this.submitForm.transferAgencyPaymentType === 'lineThailand'
    },
    onUpdateAutofillAddress (value) {
      this.isAutofillAddress = value
      this.isEditAutofillAddress = false

      if (this.isAutofillAddress) {
        this.autofillAddress()
      } else {
        this.submitForm = JSON.parse(JSON.stringify(defaultSubmitForm))
        this.$set(this, 'isJuristicCustomer', undefined)
        this.$set(this, 'customerTypeId', NORMAL_CUSTOMER_TYPE_VALUE)
        this.$set(this, 'isHeadquarter', undefined)
      }
    },
    onUpdateNewPremiumId (value) {
      this.isNewPremiumId = value
    },
    onUpdateNewOaChat (value) {
      this.isNewOaChat = value
    },
    onUpdateCustomerType (value) {
      const customerTypeValue = value
        ? this.$default.JURISTIC_CUSTOMER_TYPE_VALUE
        : this.$default.NORMAL_CUSTOMER_TYPE_VALUE
      this.$set(this.submitForm, 'customerTypeId', customerTypeValue)

      this.isJuristicCustomer = customerTypeValue !== this.$default.NORMAL_CUSTOMER_TYPE_VALUE
    },
    onUpdateCitizenId(value) {
      const filterCitizenId = this.ownLineOfficialAccounts
        .filter((item) => item.lineOfficialAccount.citizenId === value)
      if (this.$refs.citizenId.errors.length > 0) {
        if (!this.hasLineOfficialAccount || !filterCitizenId.length) {
          this.$swal({
            title: '<span style="color: #01BB00;">ไม่พบข้อมูลในระบบ</span>',
            html: 'ลูกค้าสามารถกรอกข้อมูลในระบบให้ครบถ้วนเพื่อทำรายการ',
            icon: 'success',
            iconHtml: '<img src="/img/icons/find-user-information.png" height="100" width="100" />',
            showCloseButton: true
          })
          return
        }
      }

      const lastInformation = filterCitizenId[filterCitizenId.length - 1]

      if (this.isJuristicCustomer) {
        if (lastInformation == null) {
          this.isHeadquarter = true
          this.$set(this.submitForm, 'branchNumber', HEADQUARTER_BRANCH_NUMBER)
          this.$set(this.submitForm, 'companyName', undefined)
        } else {
          const branchNumber = lastInformation?.lineOfficialAccount?.branchNumber
          this.isHeadquarter = branchNumber === HEADQUARTER_BRANCH_NUMBER
          this.$set(this.submitForm, 'branchNumber', branchNumber)
          this.$set(this.submitForm, 'companyName', lastInformation?.lineOfficialAccount?.companyName)
        }
      }

      this.$set(this.submitForm, 'firstName', lastInformation?.lineOfficialAccount?.firstName)
      this.$set(this.submitForm, 'lastName', lastInformation?.lineOfficialAccount?.lastName)
      this.$set(this.submitForm, 'email', lastInformation?.lineOfficialAccount?.email)
      this.$set(this.submitForm, 'emailQuotation', lastInformation?.lineOfficialAccount?.emailQuotation)
      this.$set(this.submitForm, 'tel', lastInformation?.lineOfficialAccount?.tel)
      this.$set(this.submitForm, 'address', lastInformation?.lineOfficialAccount?.address)
      this.$set(this.submitForm, 'street', lastInformation?.lineOfficialAccount?.street)
      this.$set(this.submitForm, 'subdistrict', lastInformation?.lineOfficialAccount?.subdistrict)
      this.$set(this.submitForm, 'district', lastInformation?.lineOfficialAccount?.district)
      this.$set(this.submitForm, 'stateProvince', lastInformation?.lineOfficialAccount?.stateProvince)
      this.$set(this.submitForm, 'zipCode', lastInformation?.lineOfficialAccount?.zipCode)
    },
    onUpdateIsHeadquarter (value) {
      this.submitForm.branchNumber = value ? this.$default.HEADQUARTER_BRANCH_NUMBER : ''
      this.isHeadquarter = this.submitForm.branchNumber === this.$default.HEADQUARTER_BRANCH_NUMBER
    },
    onAcceptLineThailand () {
      this.lineThailandActive = false
      this.isLineThailandAccepted = true
    },
    onAcceptOtherAgency () {
      this.otherAgencyActive = false
      this.isOtherAgencyAccepted = true
    },
    onCancelLineThailand () {
      this.lineThailandActive = false
      this.submitForm.transferAgencyPaymentType = undefined
    },
    onCancelOtherAgency () {
      this.otherAgencyActive = false
      this.submitForm.transferAgencyPaymentType = undefined
    },
    onSelectAddress (value) {
      this.$set(this.submitForm, 'subdistrict', value.subdistrict)
      this.$set(this.submitForm, 'district', value.district)
      this.$set(this.submitForm, 'stateProvince', value.stateProvince)
      this.$set(this.submitForm, 'zipCode', value.zipCode)
    },
    findProductByType (orderItems, productType) {
      return orderItems.find((item) => item.productType === productType)
    },
    async findRevenue () {
      const revenue = await this.fetchRevenue(this.submitForm.citizenId)
      if (revenue.NID.length > 0) {
        this.$set(this.submitForm, 'companyName', revenue.Name[0])
        this.$set(this.submitForm, 'address', revenue.HouseNumber[0])
        this.$set(this.submitForm, 'street', revenue.StreetName[0])
        this.$set(this.submitForm, 'subdistrict', revenue.Thambol[0])
        this.$set(this.submitForm, 'district', revenue.Amphur[0])
        this.$set(this.submitForm, 'stateProvince', revenue.Province[0])
        this.$set(this.submitForm, 'zipCode', revenue.PostCode[0])
      } else {
        this.$set(this.submitForm, 'companyName', undefined)
        this.$set(this.submitForm, 'address', undefined)
        this.$set(this.submitForm, 'street', undefined)
        this.$set(this.submitForm, 'subdistrict', undefined)
        this.$set(this.submitForm, 'district', undefined)
        this.$set(this.submitForm, 'stateProvince', undefined)
        this.$set(this.submitForm, 'zipCode', undefined)
      }
    },
    async routeToOrderSummary () {
      if (this.hasPackage && (!this.packagePlan || !this.packageMonth)) {
        this.$vuetify.goTo('#order-product-list')
        return
      }

      const canSubmit = await this.$refs.orderCreateForm.validate()

      if (!canSubmit) {
        this.$utils.scrollToErrorField('orderCreateForm', this)
        return
      }
      if (this.isTransferLineOfficialAccountType) {
        const findBasicId = await this.fetchLineOfficialAccountByBasicId(this.submitForm.basicId)
        if (findBasicId) {
          await this.$swal({
            icon: 'warning',
            title: 'Basic ID นี้มีในระบบแล้ว',
            html: 'กรุณาแก้ไข Basic ID ให้ถูกต้อง หรือติดต่อเจ้าหน้าที่'
          })
          this.$vuetify.goTo('#basic-id')
          return
        }
      }

      const orderItems = []
      if (this.hasPackage) {
        const packageData = {
          ...this.findPackageProduct,
          itemQty: 1,
          itemAmount: this.findPackageProduct.unitPrice,
          startedAt: this.$utils.createDayjsDate(this.packageStartedAt).toDate(),
          expiredAt: this.$utils.createDayjsDate(this.packageStartedAt).add(this.packageMonth - 1, 'month').endOf('month').toDate()
        }

        orderItems.push(packageData)
      }

      if (this.hasMessage) {
        const messageData = {
          ...this.findMessageProduct,
          itemAmount: this.totalMessagePrice,
          itemQty: parseInt(this.messageQty, 10)
        }

        orderItems.push(messageData)
      }

      if (this.hasPremiumId) {
        const premiumIdData = {
          ...this.findPremiumIdProduct,
          itemAmount: this.findPremiumIdProduct.unitPrice,
          premiumId: this.submitForm.premiumId,
          itemQty: 1,
          startedAt: this.$utils.createDayjsDate(this.premiumIdStartedAt).toDate(),
          expiredAt: this.$utils.createDayjsDate(this.premiumIdStartedAt)
            .add(1, 'year')
            .subtract(1, 'month')
            .endOf('month')
            .toDate()
        }

        orderItems.push(premiumIdData)
      }

      if (this.hasOaChat) {
        const oaChatData = {
          ...this.findOaChatProduct,
          itemAmount: this.findOaChatProduct.unitPrice,
          itemQty: 1,
          startedAt: this.$utils.createDayjsDate(this.oaChatStartedAt).toDate(),
          expiredAt: this.$utils.createDayjsDate(this.oaChatStartedAt).add(this.findOaChatProduct.oaChatMonth - 1, 'month').endOf('month').toDate()
        }

        orderItems.push(oaChatData)
      }

      // Task[7kyyaz] : เอา Agency fee 8% ออกจากระบบ

      // const findAgencyFee = this.mapProducts.find((item) => item.productType === 'fee'
      // && item.feeType === 'agency')

      // const totalOrderItemAmount = orderItems.reduce((acc, cur) => (+cur.itemAmount + acc), 0)

      // const agencyFeeData = {
      //   ...findAgencyFee,
      //   unitPrice: totalOrderItemAmount * findAgencyFee.feeValue,
      //   itemAmount: totalOrderItemAmount * findAgencyFee.feeValue,
      //   itemQty: 1
      // }

      // orderItems.push(agencyFeeData)
      this.setCreateOrderForm({
        ...this.submitForm,
        id: this.id,
        isEdit: this.isEdit,
        isAutofillAddress: this.isAutofillAddress,
        isEditAutofillAddress: this.isEditAutofillAddress,
        isTransferLineOfficialAccountType: this.isTransferLineOfficialAccountType,
        isRenew: this.isRenew,
        isNewPremiumId: this.isNewPremiumId,
        isNewOaChat: this.isNewOaChat,
        type: this.productType,
        packagePlan: this.packagePlan,
        packageMonth: this.packageMonth,
        packageStartedAt: this.packageStartedAt,
        orderItems
      })

      this.$router.push({ name: 'OrderSummary' })
    },
    checkPremiumIdValue () {
      const { premiumId } = this.submitForm
      // startWithAtSign: case 1
      // lowercase: case 2
      // numberAndSomeSpecialCharacter: case 3
      // validLength: case 4
      // noInvalidSpecialCharacter: case 5

      const clonePremiumId = premiumId
      let premiumIdSliceAtSignOut = ''

      // check start with @
      if (premiumId[0] === '@') {
        premiumIdSliceAtSignOut = clonePremiumId.slice(1)
      } else {
        premiumIdSliceAtSignOut = clonePremiumId
      }

      // case 1
      const regexCase1 = new RegExp(/^@/)
      this.premiumIdValidate.startWithAtSign = regexCase1.test(premiumId)

      // case 2
      if (premiumIdSliceAtSignOut.length === 0) {
        this.premiumIdValidate.lowercase = false
      } else {
        // check A-Z and thai alphabet
        let regexCase2 = new RegExp(/[A-Z\u0E00-\u0E7F]/)
        const result1 = !regexCase2.test(premiumIdSliceAtSignOut)
        // check a-z
        regexCase2 = new RegExp(/[a-z]/)
        const result2 = regexCase2.test(premiumIdSliceAtSignOut)

        this.premiumIdValidate.lowercase = result1 && result2
      }

      // case 3
      if (premiumIdSliceAtSignOut.length > 3) {
        this.premiumIdValidate.numberAndSomeSpecialCharacter = true
      } else {
        const regexCase3 = new RegExp(/[0-9._-]{1,3}/)
        // eslint-disable-next-line max-len
        this.premiumIdValidate.numberAndSomeSpecialCharacter = regexCase3.test(premiumIdSliceAtSignOut)
      }

      // case 4
      const regexCase4 = new RegExp(/^.{4,18}$/)
      this.premiumIdValidate.validLength = regexCase4.test(premiumIdSliceAtSignOut)

      // case 5
      if (premiumId.length === 0) {
        this.premiumIdValidate.noInvalidSpecialCharacter = true
      } else if (premiumId.length === 1 && premiumId[0] === '@') {
        this.premiumIdValidate.noInvalidSpecialCharacter = true
      } else {
        const regexCase5 = new RegExp(/[!@#$%^&*+?<>/:";',]/)
        this.premiumIdValidate.noInvalidSpecialCharacter = !regexCase5.test(premiumIdSliceAtSignOut)
      }
    },
    onClickMessageCheckbox () {
      if (!this.hasPackage && this.isAccountFreePackagePlan) {
        this.$swal({
          icon: 'warning',
          title: 'ไม่สามารถซื้อได้',
          html: 'คุณต้องมีแพ็กเกจเป็น <b>Basic</b> หรือ <b>Pro</b> ก่อนจึงจะสามารถซื้อ Message เพิ่มได้'
        })
      }
    },
    onClickOaChatCheckbox () {
      if (this.hasPackage && this.activeLineOfficialAccount.oaChatExpiredAt != null) {
        this.$swal({
          icon: 'warning',
          title: 'ไม่สามารถสั่งซื้อได้ OA Chat Package เพิ่มได้',
          html: 'เนื่องจากมี OA Chat Package อยู่แล้ว หากต้องการต่ออายุ กรุณาสั่งซื้อผ่านปุ่มต่ออายุ'
        })
      }
    }

  }
}
</script>

<style lang="scss" scoped>
  .v-tooltip__content {
    opacity: 1 !important;
    box-shadow: 0 0 4px #888888 !important;
    padding: 20px;
    top: -54px !important;
  }
  .tooltip-top::before {
    border-right: solid 20px transparent;
    border-left: solid 20px transparent;
    border-top: solid 20px #eee;
    transform: translateX(-50%);
    position: absolute;
    z-index: -21;
    content: '';
    top: 100%;
    left: 10%;
    height: 0;
    width: 0;
  }

  .text-input-error ::v-deep input {
    color: #EF2956;
  }
</style>
